/**
 * The copy registry for metric annotation tooltips — the FeedbackButton
 * pattern applied to explanations: surfaces name WHAT they're explaining
 * (kind + metric + platform), this module owns the words. One voice —
 * TEND speaking to the client — with zero copy drift across the dashboard
 * table, content cards, list rows, Published page and tended cards.
 * Reword here, every surface follows.
 *
 * These defaults are the base layer under the in-app editorial overrides
 * (SuperAdmin/CS edit tooltips in place; stored in tendv2_settings).
 * HARVEST CONVENTION: periodically fold the live overrides back in here
 * as the new shipped defaults (then clear the matching overrides) — the
 * default converges to the CS-blessed language, so "Restore TEND default"
 * always restores the last blessed copy, never a stale first draft.
 */

export interface TooltipContent {
  /** Stable annotation id — the override/edit key (absent = not editable). */
  key?: string;
  title: string;
  subtext: string;
}

const NR_CLOSER = 'TEND shows NR instead of a zero that isn’t real.';
const NO_LINK_CLICKS = 'posts carry no link, so there is nothing for a reader to click.';

/** Platform-specific NR reasons where the gap is structural — the platform
 * doesn't report the metric to anyone, not just to us. */
const NR_PLATFORM_REASONS: Record<string, Record<string, string>> = {
  bluesky: {
    impressions:
      'Bluesky doesn’t report view counts — to anyone. No publishing tool can show impressions for Bluesky posts.',
  },
  // Snapchat used to sit here ("reports view data only") — it does report
  // favorites, replies and shares; the copy was retired with the Snapchat
  // Analytics ticket (2026-10-29).
  // Networks whose posts cannot carry a link: Clicks is a structural zero,
  // shown as NR (Analytics Check, 2026-10-29). Instagram may leave this list
  // when links on posts or TEND's link-in-bio land.
  tiktok: { clicks: 'TikTok ' + NO_LINK_CLICKS },
  snapchat: { clicks: 'Snapchat ' + NO_LINK_CLICKS },
  instagram: { clicks: 'Instagram ' + NO_LINK_CLICKS },
};

const METRIC_LABELS: Record<string, string> = {
  impressions: 'impressions',
  likes: 'likes',
  comments: 'comments',
  shares: 'shares',
  clicks: 'click data',
  platform_clicks: 'click data',
  engagement: 'engagement data',
  posts: 'post data',
};

/**
 * NR ("not reported") tooltip. `platform` is the channel token/name when the
 * cell is platform-scoped (dashboard rows, Published rows); omit it on
 * cross-platform aggregates (content cards) for the generic phrasing.
 */
export function nrTooltip(metric: string, platform?: string | null, platformName?: string | null): TooltipContent {
  // Edits scope to exactly the variant being shown: platform-specific cells
  // save under nr.{platform}.{metric}, cross-platform aggregates under
  // nr.all.{metric} — a LinkedIn edit can never bleed into other channels.
  const key = `nr.${platform || 'all'}.${metric}`;
  const specific = platform ? NR_PLATFORM_REASONS[platform]?.[metric] : undefined;
  if (specific) {
    return { key, title: 'Not reported', subtext: `${specific} ${NR_CLOSER}` };
  }
  const label = METRIC_LABELS[metric] || 'this data';
  const who = platformName || (platform ? platform : null);
  return {
    key,
    title: 'Not reported',
    subtext: who
      ? `${who} doesn’t report ${label} for these posts. ${NR_CLOSER}`
      : `The platforms behind this content don’t report ${label}. ${NR_CLOSER}`,
  };
}

/** The view-only networks (Snapchat, TikTok, Threads, YouTube) count plays,
 * not impressions; the Impressions column shows their views, and this says
 * so for a client comparing against the network's own app. */
const VIEWS_NOT_IMPRESSIONS: Omit<TooltipContent, 'key'> = {
  title: 'Views',
  subtext:
    'This network counts plays, not impressions, so this is the number of times the post played.',
};

/**
 * Where a network's own number differs from the column's cross-platform
 * meaning, the cell shows the network's number with a dotted underline and
 * this definition on hover. Pinterest has saves, not shares (a Pin saved to
 * a board IS its re-share), and publishes its own Engagements figure
 * (saves + Pin clicks + outbound clicks; reactions and comments are not in
 * it), so TEND counts it Pinterest's way and says so. Snapchat counts views,
 * favorites and replies under our Impressions, Likes and Comments, and its
 * app shows unique viewers on post thumbnails, a smaller number than views
 * (Snapchat Analytics ticket, 2026-10-29). Editable in place like the NR
 * copy: keys `metric.{platform}.{metric}`.
 */
const PLATFORM_METRIC_NOTES: Record<string, Record<string, Omit<TooltipContent, 'key'>>> = {
  snapchat: {
    impressions: {
      title: 'Views',
      subtext:
        'Snapchat counts plays, not impressions, so this is the number of times the post played. The counters on Snapchat’s post thumbnails are unique viewers, a smaller number.',
    },
    likes: {
      title: 'Favorites',
      subtext: 'Snapchat’s name for likes: the number of times people favorited the post.',
    },
    comments: {
      title: 'Replies',
      subtext: 'Snapchat’s name for comments: replies sent to the post.',
    },
  },
  tiktok: { impressions: VIEWS_NOT_IMPRESSIONS },
  threads: {
    impressions: VIEWS_NOT_IMPRESSIONS,
    shares: {
      title: 'Reposts, quotes and shares',
      subtext: 'Reposts plus quote posts plus shares sent from the post. Threads shows them as three counts; TEND adds them.',
    },
  },
  youtube: { impressions: VIEWS_NOT_IMPRESSIONS },
  // Meta retired impressions in June 2026; both of its networks lead with
  // Views, and that is the figure under our Impressions column.
  facebook: {
    impressions: {
      title: 'Views',
      subtext: 'Meta retired impressions in 2026. This is Facebook’s Views: the number of times the post was played or displayed.',
    },
  },
  instagram: {
    impressions: {
      title: 'Views',
      subtext: 'Meta’s Views for Instagram: the number of times the post was played or displayed, the figure Instagram’s own Insights lead with.',
    },
  },
  twitter: {
    engagement: {
      title: 'X engagement',
      subtext:
        'Counted the way X counts it: every click on the post (link, media, profile, expand) plus likes, replies, reposts and bookmarks, so it matches X Analytics. X reports it for 30 days after posting; after that TEND keeps the last figure X gave, or, for posts first measured later than that, likes + replies + reposts + bookmarks.',
    },
    shares: {
      title: 'Reposts and quotes',
      subtext: 'Reposts plus quote posts. X shows them as two counts; TEND adds them, since a quote is a share.',
    },
  },
  bluesky: {
    shares: {
      title: 'Reposts and quotes',
      subtext: 'Reposts plus quote posts. Bluesky shows them as two counts; TEND adds them, since a quote is a share.',
    },
  },
  pinterest: {
    shares: {
      title: 'Saves',
      subtext:
        'Pinterest has no share count. This is the number of times people saved the Pin to one of their boards, Pinterest’s version of a share.',
    },
    engagement: {
      title: 'Pinterest engagement',
      subtext:
        'Counted the way Pinterest counts it: saves + Pin clicks + outbound clicks, so it matches Pinterest Analytics. Reactions and comments show in their own columns and are not part of this number.',
    },
    clicks: {
      title: 'Outbound clicks',
      subtext:
        'Reported by Pinterest: clicks that took people from the Pin to your site. Pinterest is the one channel whose clicks are not measured through TEND links.',
    },
  },
};

/**
 * The network's own definition for a platform-scoped cell (dashboard rows,
 * Published header and rows, the tended card's stats row); undefined when
 * the column means what it says, and for cross-platform scopes.
 */
export function platformMetricTooltip(metric: string, platform?: string | null): TooltipContent | undefined {
  if (!platform) return undefined;
  const note = PLATFORM_METRIC_NOTES[platform]?.[metric];
  return note ? { key: `metric.${platform}.${metric}`, ...note } : undefined;
}

/** Cross-platform aggregates (the original content card) can't tooltip per
 * network; when a Pinterest leg contributed saves to the Shares sum the
 * backend says so (`saves_as_shares`) and the cell carries this note. */
export const SHARES_INCLUDE_SAVES_TOOLTIP: TooltipContent = {
  key: 'metric.all.shares-include-saves',
  title: 'Includes Pinterest saves',
  subtext:
    'Pinterest has no share count, so its saves (Pins saved to a board) are counted here as shares.',
};

/** The original card's Clicks stat when a Pinterest leg contributed its
 * outbound clicks (`clicks_from_network`) to the TEND link-click count. */
export const CLICKS_INCLUDE_NETWORK_TOOLTIP: TooltipContent = {
  key: 'metric.all.clicks-include-network',
  title: 'Includes Pinterest outbound clicks',
  subtext:
    'Clicks on this content’s TEND short links, plus the outbound clicks Pinterest reports for its Pins (Pins carry no TEND link). Bot traffic is filtered out of the link count.',
};

/** The Overview's brand-wide Engagement card: what the number is, and the
 * one channel counted its own way. */
export const ENGAGEMENT_CARD_TOOLTIP: TooltipContent = {
  key: 'metric.all.engagement',
  title: 'Engagement',
  subtext:
    'Likes, comments, shares and saves across your channels. Pinterest and X are counted their own way to match their own analytics: Pinterest as saves + Pin clicks + outbound clicks, X as every click on the post plus likes, replies, reposts and bookmarks.',
};

export const BOT_CLICKS_TOOLTIP: TooltipContent = {
  key: 'bot-clicks',
  title: 'Bot traffic',
  subtext:
    'Automated link fetches from crawlers and platform preview services — not real readers. ' +
    'TEND filters them out of your click counts, spotting them by known bot signatures and by ' +
    'fetches that land within seconds of publishing.',
};

export function ownCommentTooltip(count: number): TooltipContent {
  return {
    key: 'own-comment',
    title: count === 1 ? 'Your 1st comment, excluded' : 'Your 1st comments, excluded',
    subtext:
      count === 1
        ? 'TEND posted the 1st comment on this for you. It’s left out of the count so comments reflect your audience, not us.'
        : `TEND posted ${count} of these as automatic 1st comments. They’re left out of the count so comments reflect your audience, not us.`,
  };
}

export const LINK_CLICKS_TOOLTIP: TooltipContent = {
  key: 'link-clicks',
  title: 'Link clicks',
  subtext:
    'Clicks on this content’s TEND short links, measured by us first-hand and attributed to the channel each link was published to. Bot traffic is filtered out.',
};
